/**
 * UGC Carousel — ugc-carousel.js  v3
 *
 * v3 changes:
 *  - Centered layout completely rewritten as a transform-based fan.
 *    No scroll container. Each card is positioned absolutely at left:50%
 *    and JS applies translateX + scale + opacity + z-index inline.
 *  - Scroll & grid layouts unchanged.
 *  - Infinite loop, video controls, lightbox, autoplay all retained.
 */

(function () {
  'use strict';

  const qs  = (s, c) => (c || document).querySelector(s);
  const qsa = (s, c) => [...(c || document).querySelectorAll(s)];

  /* ══════════════════════════════════════════════════════════════
     SECTION INIT
  ══════════════════════════════════════════════════════════════ */
  function initUGCSection(section) {
    const track   = qs('.ugc-track', section);
    const wrapper = qs('.ugc-carousel-wrapper', section);
    if (!track) return;

    const realItems = qsa('.ugc-item:not(.ugc-item--clone)', track);
    if (realItems.length === 0) return;

    const prevBtn = qs('.ugc-arrow--prev', section);
    const nextBtn = qs('.ugc-arrow--next', section);
    const dots    = qsa('.ugc-dot', section);

    const isLoop     = track.dataset.loop     === 'true';
    const isAutoplay = track.dataset.autoplay === 'true';
    const speed      = parseInt(track.dataset.autoplaySpeed, 10) || 4000;

    const layout = section.classList.contains('ugc-layout--centered') ? 'centered'
                 : section.classList.contains('ugc-layout--grid')     ? 'grid'
                 : 'scroll';

    let logicalIndex = 0;

    /* ── visible count (scroll/grid only) ───────────────────── */
    function visibleCount() {
      const mob  = window.innerWidth < 750;
      const prop = mob ? '--ugc-mobile-items' : '--ugc-desktop-items';
      return Math.max(1, parseInt(getComputedStyle(section).getPropertyValue(prop), 10) || 1);
    }

    /* ══════════════════════════════════════════════════════════
       CENTERED — transform fan
       Each real item gets a position slot relative to the active
       index. Slots: 0 = active centre, ±1 = immediate neighbours,
       ±2 = outer neighbours, etc.
    ══════════════════════════════════════════════════════════ */
    const FAN = {
      // How much to shift each slot (fraction of card width)
      // slot 0 = 0, slot ±1 = ±0.72, slot ±2 = ±1.28, slot ±3 = ±1.72
      offsetFractions: [0, 0.72, 1.28, 1.72, 2.12],
      // scale per abs(slot): 0→1, 1→0.82, 2→0.68, 3→0.58, 4→0.50
      scales:          [1, 0.82, 0.68, 0.58, 0.50],
      // opacity per abs(slot)
      opacities:       [1, 1, 1, 1, 1],
      // z-index per abs(slot) (higher = front)
      zIndexes:        [10, 7, 4, 2, 1],
    };

    function applyFan() {
      const total    = realItems.length;
      const cardW    = realItems[0].offsetWidth; // all items same width
      const wrapperH = realItems[0].offsetHeight;

      // Set wrapper height so the section sizes correctly
      if (wrapper) wrapper.style.setProperty('--ugc-center-h', wrapperH + 'px');

      realItems.forEach((item, i) => {
        // Raw slot = distance from active, wrap for shortest path
        let slot = i - logicalIndex;
        if (slot > total / 2)  slot -= total;
        if (slot < -total / 2) slot += total;

        const absSlot  = Math.abs(slot);
        const maxSlot  = FAN.offsetFractions.length - 1;
        const clampedSlot = Math.min(absSlot, maxSlot);

        const sign     = slot === 0 ? 0 : slot > 0 ? 1 : -1;
        const offsetPx = sign * FAN.offsetFractions[clampedSlot] * cardW;
        const scale    = FAN.scales[clampedSlot];
        const opacity  = FAN.opacities[clampedSlot];
        const zIndex   = FAN.zIndexes[clampedSlot];

        // Hide items too far away (> maxSlot from centre)
        const visible  = absSlot <= maxSlot;

        item.style.transform  = `translateX(${offsetPx}px) scale(${scale})`;
        item.style.opacity    = visible ? opacity : 0;
        item.style.zIndex     = zIndex;
        item.style.pointerEvents = (visible && absSlot <= 2) ? '' : 'none';
        item.style.setProperty('--item-w', cardW + 'px');
        item.classList.toggle('is-active', slot === 0);
      });
    }

    /* ── Set initial card width for centered (before first applyFan) */
    function initCenteredSizing() {
      if (layout !== 'centered') return;
      // Card width = 35% of wrapper on desktop, 75% on mobile
      const wrapW   = wrapper ? wrapper.offsetWidth : section.offsetWidth;
      const isMob   = window.innerWidth < 750;
      const cardW   = Math.round(wrapW * (isMob ? 0.72 : 0.35));
      const cardH   = Math.round(cardW * (16 / 9)); // portrait default

      realItems.forEach(item => {
        item.style.width  = cardW + 'px';
        item.style.height = cardH + 'px';
        item.style.setProperty('--item-w', cardW + 'px');
      });

      if (wrapper) {
        wrapper.style.setProperty('--ugc-center-h', cardH + 'px');
        wrapper.style.height = cardH + 'px';
      }
    }

    /* ── goTo ────────────────────────────────────────────────── */
    function goTo(li) {
      const total = realItems.length;
      logicalIndex = ((li % total) + total) % total;

      if (layout === 'centered') {
        applyFan();
      } else {
        scrollToLogical(logicalIndex);
      }
      syncUI();
    }

    /* ══════════════════════════════════════════════════════════
       SCROLL LAYOUT — clone bookend infinite loop
    ══════════════════════════════════════════════════════════ */
    let clonesBefore = 0;

    function setupClones() {
      if (!isLoop || layout !== 'scroll') return;
      qsa('.ugc-item--clone', track).forEach(el => el.remove());

      const v          = visibleCount();
      const cloneCount = Math.min(realItems.length, Math.max(v, 3));

      // Append (start copies at end)
      for (let i = 0; i < cloneCount; i++) {
        const clone = realItems[i % realItems.length].cloneNode(true);
        clone.classList.add('ugc-item--clone', 'ugc-item--clone-after');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }
      // Prepend (end copies at start)
      const frags = [];
      for (let i = cloneCount - 1; i >= 0; i--) {
        const clone = realItems[realItems.length - 1 - (i % realItems.length)].cloneNode(true);
        clone.classList.add('ugc-item--clone', 'ugc-item--clone-before');
        clone.setAttribute('aria-hidden', 'true');
        frags.push(clone);
      }
      frags.reverse().forEach(c => track.insertBefore(c, track.firstChild));
      clonesBefore = cloneCount;
    }

    function allScrollItems() { return qsa('.ugc-item', track); }

    function scrollToLogical(li, animate = true) {
      if (layout !== 'scroll') return;
      const items  = allScrollItems();
      const domIdx = clonesBefore + li;
      const target = items[domIdx];
      if (!target) return;

      track.style.scrollBehavior = animate ? 'smooth' : 'auto';
      track.scrollLeft = target.offsetLeft;
      if (!animate) { void track.scrollLeft; track.style.scrollBehavior = ''; }
    }

    function teleportIfClone() {
      if (!isLoop || layout !== 'scroll') return;
      const items      = allScrollItems();
      const realStart  = clonesBefore;
      const realEnd    = clonesBefore + realItems.length - 1;

      let nearest = realStart, minD = Infinity;
      items.forEach((item, i) => {
        const d = Math.abs(item.offsetLeft - track.scrollLeft);
        if (d < minD) { minD = d; nearest = i; }
      });

      if (nearest < realStart) {
        const equiv = realStart + (realItems.length - (realStart - nearest));
        const safeEquiv = Math.min(Math.max(equiv, realStart), realEnd);
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = items[safeEquiv].offsetLeft;
        void track.scrollLeft;
        track.style.scrollBehavior = '';
      } else if (nearest > realEnd) {
        const equiv = realStart + ((nearest - realStart) % realItems.length);
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = items[equiv].offsetLeft;
        void track.scrollLeft;
        track.style.scrollBehavior = '';
      }
    }

    /* ── scroll-end sync ─────────────────────────────────────── */
    let scrollEndTimer;
    if (layout === 'scroll') {
      track.addEventListener('scroll', () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => {
          const items = allScrollItems();
          let nearest = 0, minD = Infinity;
          items.forEach((item, i) => {
            const d = Math.abs(item.offsetLeft - track.scrollLeft);
            if (d < minD) { minD = d; nearest = i; }
          });
          const realStart = clonesBefore;
          const realEnd   = clonesBefore + realItems.length - 1;

          if (nearest >= realStart && nearest <= realEnd) {
            logicalIndex = nearest - realStart;
          } else if (nearest < realStart) {
            logicalIndex = ((nearest - realStart) + realItems.length) % realItems.length;
          } else {
            logicalIndex = (nearest - realStart) % realItems.length;
          }
          syncUI();
          teleportIfClone();
        }, 100);
      }, { passive: true });
    }

    /* ══════════════════════════════════════════════════════════
       syncUI — dots + arrows
    ══════════════════════════════════════════════════════════ */
    function syncUI() {
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === logicalIndex);
        d.setAttribute('aria-selected', i === logicalIndex ? 'true' : 'false');
      });

      if (!isLoop || layout === 'grid') {
        const v = visibleCount();
        if (prevBtn) prevBtn.disabled = logicalIndex === 0;
        if (nextBtn) nextBtn.disabled = logicalIndex >= realItems.length - v;
      } else {
        if (prevBtn) prevBtn.disabled = false;
        if (nextBtn) nextBtn.disabled = false;
      }
    }

    /* ══════════════════════════════════════════════════════════
       ARROWS + DOTS + KEYBOARD
    ══════════════════════════════════════════════════════════ */
    if (prevBtn) prevBtn.addEventListener('click', () => { stopAutoplay(); goTo(logicalIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', () => { stopAutoplay(); goTo(logicalIndex + 1); });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { stopAutoplay(); goTo(i); }));

    section.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { stopAutoplay(); goTo(logicalIndex - 1); }
      if (e.key === 'ArrowRight') { stopAutoplay(); goTo(logicalIndex + 1); }
    });

    /* ── Click a side card in centered mode to advance to it ─── */
    if (layout === 'centered') {
      realItems.forEach((item, i) => {
        item.addEventListener('click', () => {
          if (i !== logicalIndex) { stopAutoplay(); goTo(i); }
        });
      });
    }

    /* ══════════════════════════════════════════════════════════
       DRAG / SWIPE
    ══════════════════════════════════════════════════════════ */
    let dragStartX = null, isDragging = false;

    function onDragStart(x) { dragStartX = x; isDragging = true; }
    function onDragEnd(x) {
      if (!isDragging) return;
      isDragging = false;
      const delta = (dragStartX || 0) - x;
      if (Math.abs(delta) > 40) { stopAutoplay(); goTo(logicalIndex + (delta > 0 ? 1 : -1)); }
      dragStartX = null;
    }

    // Mouse
    const dragTarget = layout === 'centered' ? wrapper : track;
    dragTarget.addEventListener('mousedown',  e => { onDragStart(e.clientX); if (layout === 'scroll') track.style.cursor = 'grabbing'; });
    dragTarget.addEventListener('mousemove',  e => { if (isDragging && layout === 'scroll') track.scrollLeft -= e.movementX; });
    dragTarget.addEventListener('mouseup',    e => { onDragEnd(e.clientX); track.style.cursor = ''; });
    dragTarget.addEventListener('mouseleave', () => { isDragging = false; track.style.cursor = ''; });

    // Touch
    dragTarget.addEventListener('touchstart', e => { onDragStart(e.touches[0].clientX); }, { passive: true });
    dragTarget.addEventListener('touchend',   e => { onDragEnd(e.changedTouches[0].clientX); });

    /* ══════════════════════════════════════════════════════════
       AUTOPLAY
    ══════════════════════════════════════════════════════════ */
    let autoTimer = null;
    function startAutoplay() {
      if (!isAutoplay) return;
      autoTimer = setInterval(() => goTo(logicalIndex + 1), speed);
    }
    function stopAutoplay()  { clearInterval(autoTimer); autoTimer = null; }
    function resumeAutoplay(){ stopAutoplay(); startAutoplay(); }

    section.addEventListener('mouseenter', stopAutoplay);
    section.addEventListener('mouseleave', resumeAutoplay);
    section.addEventListener('touchstart', stopAutoplay, { passive: true });

    /* ══════════════════════════════════════════════════════════
       VIDEO CONTROLS
    ══════════════════════════════════════════════════════════ */
    function initVideoControls(card) {
      const video   = card.querySelector('video');
      const muteBtn = card.querySelector('.ugc-btn--mute');
      const playBtn = card.querySelector('.ugc-btn--play');
      if (!video) return;

      const videoAutoplay = track.dataset.videoAutoplay !== 'paused';
      video.muted = true;
      if (videoAutoplay) video.play().catch(() => {});

      if (muteBtn) {
        const iconMuted = muteBtn.querySelector('.icon-muted');
        const iconSound = muteBtn.querySelector('.icon-sound');
        function syncMuteIcon() {
          if (iconMuted) iconMuted.style.display = video.muted ? '' : 'none';
          if (iconSound) iconSound.style.display = video.muted ? 'none' : '';
        }
        syncMuteIcon();
        muteBtn.addEventListener('click', e => { e.stopPropagation(); video.muted = !video.muted; syncMuteIcon(); });
      }

      if (playBtn) {
        const iconPause = playBtn.querySelector('.icon-pause');
        const iconPlay  = playBtn.querySelector('.icon-play');
        function syncPlayIcon() {
          if (iconPause) iconPause.style.display = video.paused ? 'none' : '';
          if (iconPlay)  iconPlay.style.display  = video.paused ? ''     : 'none';
        }
        syncPlayIcon();
        playBtn.addEventListener('click', e => { e.stopPropagation(); video.paused ? video.play() : video.pause(); });
        video.addEventListener('play',  syncPlayIcon);
        video.addEventListener('pause', syncPlayIcon);
      }

      const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && videoAutoplay) video.play().catch(() => {});
        else video.pause();
      }, { threshold: 0.5 });
      io.observe(card);
    }

    qsa('.ugc-card', track).forEach(card => {
      if (!card.closest('.ugc-item--clone')) initVideoControls(card);
    });

    /* ══════════════════════════════════════════════════════════
       EXTERNAL EMBED LIGHTBOX
    ══════════════════════════════════════════════════════════ */
    qsa('.ugc-embed', section).forEach(embed => {
      const btn = embed.querySelector('.ugc-embed__play');
      if (btn) btn.addEventListener('click', () => openLightbox(embed.dataset.url));
    });

    /* ══════════════════════════════════════════════════════════
       RESIZE
    ══════════════════════════════════════════════════════════ */
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (layout === 'centered') {
          initCenteredSizing();
          applyFan();
        } else {
          setupClones();
          scrollToLogical(logicalIndex, false);
          syncUI();
        }
      }, 200);
    });

    /* ══════════════════════════════════════════════════════════
       BOOT
    ══════════════════════════════════════════════════════════ */
    if (layout === 'centered') {
      initCenteredSizing();
      applyFan();
    } else {
      setupClones();
      scrollToLogical(logicalIndex, false);
    }
    syncUI();
    startAutoplay();
  }

  /* ══════════════════════════════════════════════════════════════
     LIGHTBOX
  ══════════════════════════════════════════════════════════════ */
  function buildEmbedUrl(raw) {
    try {
      const url = new URL(raw);
      if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
        const id = url.searchParams.get('v') || url.pathname.split('/').pop();
        return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
      }
      if (url.hostname.includes('vimeo.com')) {
        const id = url.pathname.split('/').pop();
        return `https://player.vimeo.com/video/${id}?autoplay=1`;
      }
      if (url.hostname.includes('tiktok.com') || url.hostname.includes('instagram.com')) {
        window.open(raw, '_blank', 'noopener'); return null;
      }
      return raw;
    } catch { return raw; }
  }

  function openLightbox(rawUrl) {
    const embedUrl = buildEmbedUrl(rawUrl);
    if (!embedUrl) return;
    const ov = document.createElement('div');
    ov.className = 'ugc-lightbox';
    ov.innerHTML = `
      <div class="ugc-lightbox__inner">
        <button class="ugc-lightbox__close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <iframe src="${embedUrl}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen frameborder="0"></iframe>
      </div>`;
    document.body.appendChild(ov);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => ov.classList.add('is-open'));
    function close() {
      ov.classList.remove('is-open');
      ov.addEventListener('transitionend', () => { ov.remove(); document.body.style.overflow = ''; }, { once: true });
    }
    ov.querySelector('.ugc-lightbox__close').addEventListener('click', close);
    ov.addEventListener('click', e => { if (e.target === ov) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); }, { once: true });
  }

  /* ── Lightbox + clone styles ─────────────────────────────── */
  function injectLightboxStyles() {
    if (document.getElementById('ugc-lightbox-styles')) return;
    const s = document.createElement('style');
    s.id = 'ugc-lightbox-styles';
    s.textContent = `
      .ugc-lightbox{position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.9);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .3s ease}
      .ugc-lightbox.is-open{opacity:1}
      .ugc-lightbox__inner{position:relative;width:min(90vw,720px);aspect-ratio:9/16}
      @media(min-width:750px){.ugc-lightbox__inner{aspect-ratio:16/9}}
      .ugc-lightbox__inner iframe{width:100%;height:100%;border-radius:12px}
      .ugc-lightbox__close{position:absolute;top:-44px;right:0;width:36px;height:36px;background:rgba(255,255,255,.15);border:none;border-radius:50%;cursor:pointer;color:#fff;display:flex;align-items:center;justify-content:center;transition:background .2s}
      .ugc-lightbox__close:hover{background:rgba(255,255,255,.3)}
      .ugc-lightbox__close svg{width:18px;height:18px}
      .ugc-item--clone{pointer-events:none}
    `;
    document.head.appendChild(s);
  }

  /* ── Boot ─────────────────────────────────────────────────── */
  function boot() {
    injectLightboxStyles();
    qsa('.ugc-section').forEach(initUGCSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', e => {
    const sec = e.target.querySelector('.ugc-section');
    if (sec) initUGCSection(sec);
  });

})();